"use client";

import { useState } from "react";
import { useFormState, useFormStatus } from "react-dom";
import { Loader2, Lock, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  loginAction,
  signupAction,
  type AuthResult,
} from "@/app/actions/auth";
import { getSupabaseBrowserClient } from "@/lib/auth/supabase-browser";
import { siteConfig } from "@/lib/config";

type Mode = "login" | "signup";

interface LoginFormProps {
  /** Forwarded from the page so we can surface "?error=..." copy. */
  errorParam?: string;
  /** Starting mode — "login" by default; "signup" used by /registro. */
  initialMode?: Mode;
  /**
   * Signup intent — when set to "agent", the signup form posts an
   * intent=agent hidden field AND Google OAuth uses
   * `?intent=agent` on the redirect URL so /auth/callback can
   * auto-assign the 'agent' role on first confirmation.
   */
  signupIntent?: "agent";
  /**
   * Where to send the user after successful auth (same-origin only;
   * sanitized in /auth/callback). Threaded through Google OAuth as a
   * redirect query param AND through email signup as a hidden form
   * field so signupAction can stash it in user_metadata for the
   * email-confirmation gap.
   */
  returnTo?: string;
}

const ERROR_COPY: Record<string, string> = {
  expired: "El enlace expiró o ya fue usado. Intenta de nuevo.",
  unknown: "No pudimos completar la operación. Intenta de nuevo.",
};

export function LoginForm({
  errorParam,
  initialMode = "login",
  signupIntent,
  returnTo,
}: LoginFormProps) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);

  const action = mode === "login" ? loginAction : signupAction;
  const [state, formAction] = useFormState<AuthResult | null, FormData>(
    action,
    null
  );

  const fieldErrors =
    state?.status === "validation_error" ? state.fieldErrors : {};

  async function continueWithGoogle() {
    setGoogleError(null);
    setGoogleLoading(true);
    const supabase = getSupabaseBrowserClient();
    const params = new URLSearchParams();
    if (mode === "signup" && signupIntent) params.set("intent", signupIntent);
    if (returnTo) params.set("returnTo", returnTo);
    const qs = params.toString();
    const redirectTo = `${siteConfig.url}/auth/callback${qs ? `?${qs}` : ""}`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
      },
    });
    if (error) {
      setGoogleError(error.message);
      setGoogleLoading(false);
    }
    // On success, the browser is being redirected to Google — no state to update.
  }

  if (state?.status === "signup_success_check_email") {
    return (
      <div
        role="status"
        className="rounded-2xl bg-emerald/10 p-5 ring-1 ring-emerald/30"
      >
        <p className="flex items-start gap-2 text-sm leading-relaxed text-ink">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-emerald" aria-hidden />
          <span>
            <strong className="text-ink">Revisa tu correo.</strong> Te enviamos
            un enlace para confirmar tu cuenta. Una vez que hagas clic, podrás
            iniciar sesión.
          </span>
        </p>
      </div>
    );
  }

  const initialError = errorParam
    ? ERROR_COPY[errorParam] ?? ERROR_COPY.unknown
    : null;

  return (
    <div className="space-y-5">
      {initialError && (
        <p
          role="alert"
          className="rounded-xl bg-coral/10 px-4 py-3 text-sm text-coral ring-1 ring-coral/30"
        >
          {initialError}
        </p>
      )}
      {state?.status === "error" && (
        <p
          role="alert"
          className="rounded-xl bg-coral/10 px-4 py-3 text-sm text-coral ring-1 ring-coral/30"
        >
          {state.message}
        </p>
      )}

      <button
        type="button"
        onClick={continueWithGoogle}
        disabled={googleLoading}
        className="flex w-full items-center justify-center gap-3 rounded-xl border border-ink-200 bg-white px-4 py-3 text-sm font-semibold text-ink shadow-sm transition hover:border-ink-300 hover:shadow disabled:opacity-60"
      >
        {googleLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        ) : (
          <GoogleGlyph />
        )}
        Continuar con Google
      </button>

      {googleError && (
        <p role="alert" className="text-xs text-coral">
          {googleError}
        </p>
      )}

      <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-ink-500">
        <span className="h-px flex-1 bg-ink-200" aria-hidden />
        <span>o con email</span>
        <span className="h-px flex-1 bg-ink-200" aria-hidden />
      </div>

      <form action={formAction} className="space-y-4" noValidate>
        {/* Carries signup intent through the email-confirmation gap.
            Read by signupAction → user_metadata → /auth/callback. */}
        {mode === "signup" && signupIntent && (
          <input type="hidden" name="intent" value={signupIntent} />
        )}
        {/* Same idea for returnTo — login flow can use it immediately
            (no email gap); signup flow stashes it in user_metadata. */}
        {returnTo && (
          <input type="hidden" name="returnTo" value={returnTo} />
        )}
        <Field
          htmlFor="auth-email"
          label="Email"
          error={fieldErrors.email}
        >
          <Input
            id="auth-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="tu@email.com"
          />
        </Field>
        <Field
          htmlFor="auth-password"
          label="Contraseña"
          error={fieldErrors.password}
          hint={mode === "signup" ? "Mínimo 8 caracteres" : undefined}
        >
          <Input
            id="auth-password"
            name="password"
            type="password"
            required
            autoComplete={
              mode === "signup" ? "new-password" : "current-password"
            }
            placeholder="••••••••"
          />
        </Field>
        <SubmitButton mode={mode} />
      </form>

      <p className="text-center text-sm text-ink-500">
        {mode === "login" ? (
          <>
            ¿No tienes cuenta?{" "}
            <button
              type="button"
              onClick={() => setMode("signup")}
              className="font-semibold text-coral underline-offset-4 hover:underline"
            >
              Crear cuenta
            </button>
          </>
        ) : (
          <>
            ¿Ya tienes cuenta?{" "}
            <button
              type="button"
              onClick={() => setMode("login")}
              className="font-semibold text-coral underline-offset-4 hover:underline"
            >
              Iniciar sesión
            </button>
          </>
        )}
      </p>
    </div>
  );
}

function SubmitButton({ mode }: { mode: Mode }) {
  const { pending } = useFormStatus();
  return (
    <Button
      type="submit"
      variant="primary"
      size="lg"
      disabled={pending}
      className="w-full"
    >
      {pending ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          {mode === "login" ? "Entrando…" : "Creando cuenta…"}
        </>
      ) : (
        <>
          <Lock className="h-5 w-5" aria-hidden />
          {mode === "login" ? "Iniciar sesión" : "Crear cuenta"}
        </>
      )}
    </Button>
  );
}

function GoogleGlyph() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 48 48"
      aria-hidden
      className="shrink-0"
    >
      <path
        fill="#FFC107"
        d="M43.6 20.4H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.3 29.3 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.7 0 19.5-8.7 19.5-19.5 0-1.3-.1-2.3-.4-3.6z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.3 29.3 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 43.5c5.2 0 9.9-1.8 13.5-4.8l-6.2-5.2c-2 1.4-4.5 2.3-7.3 2.3-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.6 39 16.3 43.5 24 43.5z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.4H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.2 5.2c3.6-3.4 5.7-8.4 5.7-14 0-1.3-.1-2.3-.4-3.6z"
      />
    </svg>
  );
}
